import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';
import { Stack, Typography } from '@mui/material';

import type { ReceptionFeature } from '@/api/domains/drugs.types';
import { RHFIntegerTextField, RHFTextField } from '@/components/form-controls';
import { RHFReceptionFeaturesAutocomplete } from '@/components/form-controls/RHFReceptionFeaturesAutocomplete';
import {
  NamesByReceptionFeaturesAliases,
  RECEPTION_FEATURES_ALIAS_CHILDREN,
  RECEPTION_FEATURES_ALIAS_PREGNANTS,
  RECEPTION_FEATURES_LEVEL_ALLOWED,
  RECEPTION_FEATURES_LEVEL_WITH_CAUTION,
} from '@/shared/constants/drugs.constants';

// ----------------------------------------------------------------------

type ReceptionFeatureFieldsProps = {
  namePrefix: string;
  receptionFeature: ReceptionFeature;
};

// ----------------------------------------------------------------------

type ReceptionMinimumAgeFieldProps = {
  namePrefix: string;
  minimumAge: number;
};

const ReceptionMinimumAgeField = (props: ReceptionMinimumAgeFieldProps) => {
  const { namePrefix, minimumAge } = props;
  const { t } = useTranslation('nomenclature-page');

  return (
    <Stack direction="row" spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {t('modals.reception-features.age.from')}
        </Typography>
        <RHFIntegerTextField
          id={`reception_features.${namePrefix}.minimum_age`}
          name={`reception_features.${namePrefix}.minimum_age`}
          variant="standard"
          sx={{ maxWidth: '24px' }}
        />
        <Typography variant="body2" color="text.secondary">
          {t('modals.reception-features.age.years')}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <RHFIntegerTextField
          id={`reception_features.${namePrefix}.minimum_month`}
          name={`reception_features.${namePrefix}.minimum_month`}
          variant="standard"
          isAllowed={({ floatValue = 0 }) => (minimumAge ? floatValue <= 11 : floatValue <= 18)}
          sx={{ maxWidth: '24px' }}
        />
        <Typography variant="body2" color="text.secondary">
          {t('modals.reception-features.age.months')}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const ReceptionFeaturesFormItem = (props: ReceptionFeatureFieldsProps) => {
  const { receptionFeature, namePrefix } = props;

  const { watch, setValue } = useFormContext();

  const receptionFeatureLevel = watch(`reception_features.${namePrefix}.level`);
  const minimumAge = watch(`reception_features.${namePrefix}.minimum_age`);
  const minimumMonth = watch(`reception_features.${namePrefix}.minimum_month`);
  const receptionFeatureTitle = NamesByReceptionFeaturesAliases[receptionFeature.subject_alias];

  const isMinimumAgeVisible =
    receptionFeature.subject_alias === RECEPTION_FEATURES_ALIAS_CHILDREN &&
    receptionFeatureLevel === RECEPTION_FEATURES_LEVEL_ALLOWED;
  const isCustomTextFieldVisible =
    receptionFeatureLevel === RECEPTION_FEATURES_LEVEL_WITH_CAUTION ||
    (receptionFeatureLevel === RECEPTION_FEATURES_LEVEL_ALLOWED &&
      receptionFeature.subject_alias === RECEPTION_FEATURES_ALIAS_PREGNANTS);

  useUpdateEffect(() => {
    if (!isCustomTextFieldVisible) {
      setValue(`reception_features.${namePrefix}.custom_text`, '');
    }
  }, [isCustomTextFieldVisible]);

  useUpdateEffect(() => {
    if (!isMinimumAgeVisible) {
      setValue(`reception_features.${namePrefix}.minimum_age`, 0);
    }
  }, [isMinimumAgeVisible]);

  useUpdateEffect(() => {
    if (Number(minimumAge) > 0 && Number(minimumMonth) > 11) {
      setValue(`reception_features.${namePrefix}.minimum_month`, 0);
    }
  }, [minimumAge, minimumMonth]);

  if (!receptionFeatureTitle) return null;

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="subtitle2" sx={theme => ({ flex: `0 0 calc((100% - (2 * ${theme.spacing(1)})) / 4)` })}>
        {receptionFeatureTitle}
      </Typography>
      <RHFReceptionFeaturesAutocomplete
        id={`reception_features.${namePrefix}.level`}
        name={`reception_features.${namePrefix}.level`}
        sx={theme => ({ flex: `0 0 calc((100% - (2 * ${theme.spacing(1)})) / 3)` })}
      />
      {isMinimumAgeVisible && <ReceptionMinimumAgeField minimumAge={Number(minimumAge)} namePrefix={namePrefix} />}
      {isCustomTextFieldVisible && (
        <RHFTextField
          name={`reception_features.${namePrefix}.custom_text`}
          id={`reception_features.${namePrefix}.custom_text`}
          variant="standard"
        />
      )}
    </Stack>
  );
};
